import React, { useState } from 'react';
import FormField from './FormField';
import { validateForm } from '../../utils/formValidation';

function PreAssistantForm({ projectConfig, projectId, onSubmit }) {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Check localStorage first, then system preference, default to light
    const saved = localStorage.getItem('formTheme');
    if (saved) return saved === 'dark';
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  const toggleTheme = () => {
    setIsDarkMode(prev => {
      const newValue = !prev;
      localStorage.setItem('formTheme', newValue ? 'dark' : 'light');
      return newValue;
    });
  };

  const handleFieldChange = (fieldId, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldId]: value
    }));
    
    if (errors[fieldId]) {
      setErrors(prev => ({
        ...prev,
        [fieldId]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validation = validateForm(formData, projectConfig.formConfig.fields);
    
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }
    
    setIsSubmitting(true);
    
    const params = new URLSearchParams();
    Object.entries(formData).forEach(([key, value]) => {
      if (value && value.toString().trim() !== '') {
        params.append(key, value);
      }
    });
    
    const newUrl = `/${projectId}?${params.toString()}`;
    window.location.href = newUrl;
  };

  const isFormValid = () => {
    const validation = validateForm(formData, projectConfig.formConfig.fields);
    return validation.isValid;
  };

  const branding = projectConfig.formConfig.branding;
  
  // Theme-aware colors
  const theme = {
    bg: isDarkMode ? '#0a0a0a' : '#ffffff',
    cardBg: isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
    cardBorder: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
    text: isDarkMode ? '#ffffff' : '#1a1a1a',
    textSecondary: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
    textMuted: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.4)',
    inputBg: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#ffffff',
    inputBorder: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.15)',
    inputText: isDarkMode ? '#ffffff' : '#1a1a1a',
    disabledBg: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
    disabledText: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.bg,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '20px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      transition: 'background-color 0.3s ease'
    }}>
      {/* Theme Toggle Button */}
      <button
        onClick={toggleTheme}
        aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
        style={{
          position: 'fixed',
          top: '16px',
          right: '16px',
          zIndex: 1000,
          width: '44px',
          height: '44px',
          borderRadius: '50%',
          border: `1px solid ${theme.cardBorder}`,
          backgroundColor: theme.cardBg,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.2s ease',
        }}
      >
        {isDarkMode ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="5"/>
            <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/>
          </svg>
        ) : (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
          </svg>
        )}
      </button>

      {/* Subtle gradient overlay */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: isDarkMode 
          ? `radial-gradient(ellipse at 50% 0%, rgba(249, 115, 22, 0.08) 0%, transparent 50%),
             radial-gradient(ellipse at 0% 100%, rgba(249, 115, 22, 0.04) 0%, transparent 40%)`
          : `radial-gradient(ellipse at 50% 0%, rgba(249, 115, 22, 0.05) 0%, transparent 50%),
             radial-gradient(ellipse at 100% 100%, rgba(249, 115, 22, 0.03) 0%, transparent 40%)`,
        pointerEvents: 'none',
        zIndex: 0
      }} />
      
      <div style={{
        position: 'relative',
        zIndex: 1,
        width: '100%',
        maxWidth: '480px'
      }}>
        {/* Logo */}
        <div style={{ 
          textAlign: 'center', 
          marginBottom: '48px' 
        }}>
          <img 
            src={branding.logo} 
            alt={`${projectConfig.name} Logo`}
            style={{
              maxWidth: '200px',
              maxHeight: '60px',
              objectFit: 'contain',
              opacity: 0.95,
              filter: isDarkMode ? 'none' : 'none'
            }}
          />
        </div>

        {/* Card */}
        <div style={{
          backgroundColor: theme.cardBg,
          borderRadius: '16px',
          border: `1px solid ${theme.cardBorder}`,
          padding: '40px',
          backdropFilter: 'blur(20px)',
          boxShadow: isDarkMode ? 'none' : '0 4px 24px rgba(0,0,0,0.06)'
        }}>
          {/* Title */}
          <h1 style={{
            color: theme.text,
            marginBottom: '8px',
            marginTop: 0,
            fontSize: '28px',
            fontWeight: '600',
            letterSpacing: '-0.02em',
            textAlign: 'center'
          }}>
            Start Your Survey
          </h1>
          
          <p style={{
            color: theme.textSecondary,
            marginBottom: '32px',
            fontSize: '15px',
            fontWeight: '400',
            textAlign: 'center',
            lineHeight: '1.5'
          }}>
            Enter your details to begin
          </p>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            {projectConfig.formConfig.fields.map((field, index) => (
              <div key={field.id} style={{ marginBottom: '20px' }}>
                <FormField
                  field={field}
                  value={formData[field.id]}
                  onChange={handleFieldChange}
                  error={errors[field.id]}
                  branding={branding}
                  isDarkMode={isDarkMode}
                  theme={theme}
                />
              </div>
            ))}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={!isFormValid() || isSubmitting}
              style={{
                width: '100%',
                padding: '16px 24px',
                marginTop: '24px',
                backgroundColor: isFormValid() ? branding.primaryColor : theme.disabledBg,
                color: isFormValid() ? '#ffffff' : theme.disabledText,
                border: 'none',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: isFormValid() ? 'pointer' : 'not-allowed',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                if (isFormValid()) {
                  e.target.style.backgroundColor = '#ea580c';
                  e.target.style.transform = 'translateY(-1px)';
                }
              }}
              onMouseLeave={(e) => {
                if (isFormValid()) {
                  e.target.style.backgroundColor = branding.primaryColor;
                  e.target.style.transform = 'translateY(0)';
                }
              }}
            >
              {isSubmitting ? (
                <>
                  <span style={{ 
                    width: '18px', 
                    height: '18px', 
                    border: '2px solid rgba(255,255,255,0.3)', 
                    borderTop: '2px solid #ffffff', 
                    borderRadius: '50%', 
                    animation: 'spin 0.8s linear infinite'
                  }} />
                  Processing...
                </>
              ) : (
                <>
                  Start Survey
                  <svg 
                    width="18" 
                    height="18" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  >
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </>
              )}
            </button>

            {/* Validation hint */}
            {!isFormValid() && Object.keys(formData).length > 0 && (
              <p style={{
                textAlign: 'center',
                color: theme.textMuted,
                fontSize: '13px',
                marginTop: '16px',
                marginBottom: 0
              }}>
                Please complete all required fields
              </p>
            )}
          </form>
        </div>

        {/* Footer */}
        <p style={{
          textAlign: 'center',
          color: theme.textMuted,
          fontSize: '12px',
          marginTop: '24px',
          lineHeight: '1.5'
        }}>
          Your responses are confidential
        </p>
      </div>

      {/* Spinner animation */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default PreAssistantForm;
